/**
 * Staff Areas & Specializations publish-and-update: after `PUT /clusters/publish`, enqueue
 * `update-pos-student-plans` per pathway (specialization) so in-progress student plans pick up
 * the latest published program version (same data-ingest channel as Plan of Study).
 */

import type { AxiosInstance } from 'axios';
import { getDataIngestUpdatePosStudentPlansPath } from './api-paths';
import { requireNonEmptySchoolNidsForPublishAndUpdate } from './plan-publish-and-update';

export interface ClusterPathwayRef {
  id?: string;
  name?: string;
}

/** Unique non-empty pathway ids in first-seen order. */
export function extractPathwayIdsFromCluster(pathways: ClusterPathwayRef[] | undefined): string[] {
  if (!Array.isArray(pathways) || pathways.length === 0) {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const pathway of pathways) {
    const id = String(pathway?.id ?? '').trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}

function pathwayDisplayName(pathwayId: string, pathways: ClusterPathwayRef[] | undefined): string {
  const match = pathways?.find((p) => String(p?.id ?? '').trim() === pathwayId);
  const name = match?.name?.trim();
  return name && name.length > 0 ? name : pathwayId;
}

/**
 * Enqueues one immediate `update-pos-student-plans` job per pathway program id (same channel as POS
 * publish-and-update). Schools are resolved by the existing import worker via
 * `getSchoolsForPOSUpdate(pathwayId)` after planning-engine matches program id at any `programs[]`
 * index. `fileVersionId` satisfies the ingest payload contract for immediate jobs.
 */
export async function enqueueUpdateStudentPlansForPathways(
  apiClient: AxiosInstance,
  input: {
    pathwayIds: readonly string[];
    pathways?: ClusterPathwayRef[];
    checkedSchoolNids: readonly string[];
  }
): Promise<number> {
  const schools = requireNonEmptySchoolNidsForPublishAndUpdate(input.checkedSchoolNids);
  const pathwayIds = input.pathwayIds.map((id) => String(id).trim()).filter(Boolean);
  if (pathwayIds.length === 0) {
    return 0;
  }

  const ingestPath = getDataIngestUpdatePosStudentPlansPath();
  let queued = 0;

  for (const pathwayId of pathwayIds) {
    const planOfStudyName = pathwayDisplayName(pathwayId, input.pathways);
    await apiClient.post(ingestPath, {
      payload: {
        planId: pathwayId,
        schools,
        planOfStudyName,
        fileVersionId: pathwayId,
      },
    });
    queued += 1;
  }

  return queued;
}
